interface Claim {
    icon_image?: string;
    title?: string;
}

class QuickView {
    private modal: HTMLElement | null;
    private overlay: HTMLElement | null;
    private closeBtn: HTMLElement | null;
    private productContainer: HTMLElement | null;
    private loadingContainer: HTMLElement | null;

    constructor() {
        this.modal = document.getElementById('quickview-modal');
        this.overlay = this.modal?.querySelector('.quickview-modal__overlay') || null;
        this.closeBtn = this.modal?.querySelector('.quickview-modal__close') || null;
        this.productContainer = this.modal?.querySelector('.quickview-modal__product') || null;
        this.loadingContainer = this.modal?.querySelector('.quickview-modal__loading') || null;

        this.init();
    }

    private init(): void {
        this.setupEventListeners();
    }

    private setupEventListeners(): void {
        document.addEventListener('click', (e) => {
            const btn = (e.target as HTMLElement).closest('.quickview-btn');
            if (btn) {
                e.preventDefault();
                const handle = btn.getAttribute('data-product-handle');
                const claimsData = btn.getAttribute('data-product-claims');

                if (handle) {
                    let claims: Claim[] = [];
                    if (claimsData) {
                        try {
                            claims = JSON.parse(claimsData);
                        } catch (e) {
                            console.error('Error parsing claims:', e);
                        }
                    }
                    this.openQuickView(handle, claims);
                }
            }
        });

        this.closeBtn?.addEventListener('click', () => {
            this.closeQuickView();
        });

        this.overlay?.addEventListener('click', () => {
            this.closeQuickView();
        });

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.modal?.style.display === 'flex') {
                this.closeQuickView();
            }
        });

        document.addEventListener('click', (e) => {
            const btn = (e.target as HTMLElement).closest('.quickview-product__quantity .qty-btn');
            if (!btn) return;

            const wrapper = btn.closest('.quickview-product__quantity');
            const input = wrapper?.querySelector<HTMLInputElement>('.qty-input');
            if (!input) return;

            const current = parseInt(input.value || '1', 10);
            const action = btn.getAttribute('data-action');

            if (action === 'increase') {
                input.value = String(current + 1);
            }

            if (action === 'decrease' && current > 1) {
                input.value = String(current - 1);
            }
        });

        document.addEventListener('click', (e) => {
            const btn = (e.target as HTMLElement).closest('.quickview-product__add-to-bundle');
            if (!btn) return;

            e.preventDefault();

            const variantId = btn.getAttribute('data-variant-id');
            const title = btn.getAttribute('data-title');
            const price = btn.getAttribute('data-price');
            const percentageDiscount = btn.getAttribute('data-diff');
            const image = btn.getAttribute('data-image') || '';

            const qtyInput = btn.closest('.quickview-product__actions')
                ?.querySelector<HTMLInputElement>('.qty-input');
            const quantity = qtyInput ? parseInt(qtyInput.value || '1', 10) : 1;

            const byobInstance = (window as any).byobInstance;

            if (!byobInstance) {
                if (window.addToBundle) {
                    const steps = document.querySelectorAll('.byob-page__step');
                    const selectedSteps = document.querySelectorAll('.byob-page__step.selected');
                    const currentStep = selectedSteps.length;

                    window.addToBundle(currentStep, {
                        id: variantId || '',
                        title: title || '',
                        price: price || '',
                        quantity: quantity,
                        percentage_discount: percentageDiscount || '0',
                        image: image,
                    });
                    this.closeQuickView();
                    this.scrollToSteps();
                } else {
                    alert('Error: BYOB system not initialized properly');
                }
                return;
            }

            const stepIndex = byobInstance.getCurrentStepIndex
                ? byobInstance.getCurrentStepIndex()
                : 0;

            const maxSteps = byobInstance.maxSteps ??
                document.querySelectorAll('.byob-page__step').length;

            if (stepIndex >= maxSteps) {
                this.closeQuickView();
                return;
            }

            if (window.addToBundle && variantId && title && price) {
                window.addToBundle(stepIndex, {
                    id: variantId,
                    title: title,
                    price: price,
                    quantity: quantity,
                    percentage_discount: percentageDiscount || '0',
                    image: image,
                });

                this.closeQuickView();
                this.scrollToSteps();
            }
        });
    }

    private scrollToSteps(): void {
        const stepsElement = document.querySelector('.byob-page__content--steps');
        if (stepsElement) {
            const header = document.querySelector('header') || document.querySelector('.header');
            const headerHeight = header ? (header as HTMLElement).offsetHeight : 78;
            const marginOffset = 20;

            const elementTop = (stepsElement as HTMLElement).getBoundingClientRect().top + window.pageYOffset;
            const scrollPosition = Math.max(0, elementTop - headerHeight - marginOffset);

            window.scrollTo({
                top: scrollPosition,
                behavior: 'smooth',
            });
        }
    }

    private async openQuickView(handle: string, claims: Claim[]): Promise<void> {
        if (!this.modal || !this.productContainer || !this.loadingContainer) return;

        this.modal.style.display = 'flex';
        this.loadingContainer.style.display = 'flex';
        this.productContainer.style.display = 'none';
        document.body.style.overflow = 'hidden';

        try {
            const response = await fetch(`/products/${handle}.js`);
            const product = await response.json();

            const html = this.buildProductHTML(product, claims);

            this.productContainer.innerHTML = html;
            this.loadingContainer.style.display = 'none';
            this.productContainer.style.display = 'block';
        } catch (error) {
            console.error('Error loading quick view:', error);
            this.closeQuickView();
        }
    }

    private buildProductHTML(product: any, claims: Claim[]): string {
        const title = product.title;
        const description = this.stripHTML(product.description || '');
        const variant = product.variants[0];
        const rawImage = variant.featured_image?.src || product.featured_image || product.images?.[0] || '';
        const image = rawImage.startsWith('//') ? `https:${rawImage}` : rawImage;
        const price = this.formatMoney(variant.price);

        const claimsHTML = claims && claims.length > 0
            ? claims.map(claim => `
                <div class="claims-product">
                    <div class="claims-product__content">
                        <div class="claims-product__icon">
                            ${claim.icon_image
                    ? `<img class="claims-product__icon-img" src="${claim.icon_image}" alt="${claim.title || ''}" width="30" height="30">`
                    : ''
                }
                        </div>
                        <div class="claims-product__text">
                            <h3 class="claims-product__text-title">${claim.title || ''}</h3>
                        </div>
                    </div>
                </div>
            `).join('')
            : '';

        return `
            <div class="quickview-product">
                <div class="quickview-product__image">
                    <img src="${image}" alt="${title}">
                </div>

                <div class="quickview-product__info">
                    <h2 class="quickview-product__title">${title}</h2>

                    <div class="quickview-product__description">
                        ${description}
                    </div>

                    ${claimsHTML ? `
                        <div class="main-product__claims">
                            ${claimsHTML}
                        </div>
                    ` : ''}

                    <div class="quickview-product__actions">
                        <div class="quickview-product__quantity">
                            <button type="button" class="qty-btn" data-action="decrease">−</button>
                            <input type="number" class="qty-input" value="1" min="1" step="1">
                            <button type="button" class="qty-btn" data-action="increase">+</button>
                        </div>

                        <button
                            class="quickview-product__add-to-bundle btn"
                            data-variant-id="${variant.id}"
                            data-title="${title}"
                            data-price="${price}"
                            data-diff="20"
                            data-image="${image}"
                            type="button"
                        >
                            ADD TO BUNDLE
                        </button>
                    </div>
                </div>
            </div>
        `;
    }

    private formatMoney(cents: number): string {
        return `$${(cents / 100).toFixed(2)}`;
    }

    private stripHTML(html: string): string {
        const tmp = document.createElement('DIV');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    }

    private closeQuickView(): void {
        if (!this.modal) return;

        this.modal.style.display = 'none';
        document.body.style.overflow = '';

        if (this.productContainer) {
            this.productContainer.innerHTML = '';
        }
    }
}

export function initQuickView(): QuickView {
    return new QuickView();
}

export default QuickView;